import { useEffect, useRef } from "react";
import { willTokenExpireWithin } from "@maeiz/core";
import { authApi } from "@api/modules/auth.api";
import { useAuthStore } from "@stores/auth";

const REFRESH_WHEN_WITHIN_SECONDS = 5 * 60;
const CHECK_INTERVAL_MS = 60 * 1000;

/**
 * Keeps the access token fresh while the app is open: on mount, on an interval,
 * and whenever the tab regains focus, it refreshes the token once it is within
 * REFRESH_WHEN_WITHIN_SECONDS of expiry. An already-expired token is cleared.
 */
export const useAuthRefresh = () => {
    const inFlight = useRef(false);

    useEffect(() => {
        const maybeRefresh = async () => {
            const { token, isAuthenticated } = useAuthStore.getState();
            if (!token || !isAuthenticated || inFlight.current) {
                return;
            }
            if (!willTokenExpireWithin(token, REFRESH_WHEN_WITHIN_SECONDS)) {
                return;
            }
            inFlight.current = true;
            try {
                const result = await authApi.refreshToken();
                if (result?.token) {
                    const nextUser = result.user ?? useAuthStore.getState().user;
                    if (nextUser) {
                        useAuthStore.getState().setAuth(nextUser, result.token);
                    }
                }
            } catch {
                /* a 401 here is cleared by the response interceptor */
            } finally {
                inFlight.current = false;
            }
        };

        void maybeRefresh();
        const interval = window.setInterval(() => {
            void maybeRefresh();
        }, CHECK_INTERVAL_MS);
        const onVisible = () => {
            if (document.visibilityState === "visible") {
                void maybeRefresh();
            }
        };
        window.addEventListener("focus", onVisible);
        document.addEventListener("visibilitychange", onVisible);
        return () => {
            window.clearInterval(interval);
            window.removeEventListener("focus", onVisible);
            document.removeEventListener("visibilitychange", onVisible);
        };
    }, []);
};
